import { ChangeEvent } from 'react';
import CloseButton from '@/components/base/CloseButton';
import VisionSettingsPanel from '@/components/private/VisionDeficiencyEmulator/SettingsPanel';
import { formatTimestamp } from '@/managers/VideoPlayer/duration';
import { Builds } from '@/pages/games/play/[...slugsIds]';
import { PbsKidsPage, SpringRollConnectBuild } from '@/types/pbskids-graph';
import { CmsCacheData, getFriendlyDate, PageCacheData, PanelStates } from '.';
import styles from './PageInfo.module.scss';

type PageInfoViewProps = {
  data: {
    isCmsPreview?: boolean,
    draft?: PbsKidsPage | null,
    drafts?: PbsKidsPage[],
    build?: SpringRollConnectBuild | null,
    builds?: Builds,
    pageCacheData: PageCacheData,
    cmsCacheData?: CmsCacheData,
  },
  states: {
    panelState: PanelStates,
    showDevUI: boolean,
    showThemeDevUI: boolean,
  }
  actions: {
    openPageInfoPanel: (state: PanelStates) => void,
    closePageInfoPanel: () => void,
    toggleShowPerformanceCheck: (e: ChangeEvent) => void,
    toggleShowThemeDebugger: (e: ChangeEvent) => void,
    logout: () => void,
  }
};

const formatDraftLabel = (pageDraft: PbsKidsPage) => {
  if (!pageDraft) return <></>;

  const formattedTime= pageDraft.dateUpdated ? getFriendlyDate(pageDraft.dateUpdated) : 'unknown';

  return <>
    <strong>{pageDraft.draftName}</strong> - <small>{formattedTime}</small>
  </>;
};

const formattedBuildLabel = (build?: SpringRollConnectBuild) => {
  if (!build) return <></>;

  const formattedTime= build.date ? getFriendlyDate(build.date) : 'unknown';

  return <>
    <strong>Commit:</strong> #{build.hash?.substring(0, 6)} {build.branch && <>[{build.branch}]</>}<br/>
    <strong>Build Date:</strong> <small>{formattedTime}</small><br/>

    <div className={styles.buildLinks}>
      <a className={styles.buildLink} href={`?version=${build.id}`}>Release Build</a>
      <a className={styles.buildLink} href={`?version=${build.id}&debug`}>Debug Build</a>
    </div>
  </>;
};

const formattedSimpleBuildLabel = (build?: SpringRollConnectBuild) => {
  if (!build) return <></>;

  const formattedTime= build.date ? getFriendlyDate(build.date) : 'unknown';

  return <>
    <div className={styles.justifyBetween}>
      <strong>#{build.hash?.substring(0, 6)}</strong>
      <small>{formattedTime}</small>
    </div>

    <div className={styles.justifyBetween}>
      <a className={styles.buildLink} href={`?version=${build.id}`}>Release Build</a>
      <a className={styles.buildLink} href={`?version=${build.id}&debug`}>Debug Build</a>
    </div>
  </>;
};

const messageLine = ( message: string | null, label = '', title = '') => {
  if (!message) return <></>;

  return <dd title={title}>
    {label ? label + ': ' : ''}{message}
  </dd>;
};

export default function PageInfoView( { data, states, actions }: PageInfoViewProps) {
  return <section className={
    `${styles.pageInfoTab} ${states.panelState !== PanelStates.closed ? styles.open : '' }`
  }>
    <header className={styles.header}>
      <h2 className={styles.title + (states.panelState === PanelStates.info ? ' ' + styles.selected : '')}
        onClick={actions.openPageInfoPanel.bind(null, PanelStates.info)}
      >
        Info
      </h2>

      { states.panelState === PanelStates.closed && <span>&</span>}

      <h2 className={styles.title + (states.panelState === PanelStates.tools ? ' ' + styles.selected : '')}
        onClick={actions.openPageInfoPanel.bind(null, PanelStates.tools)}
      >
        { states.panelState !== PanelStates.closed && 'Dev '}
        Tools
      </h2>

      { !data.isCmsPreview &&
        <h2 className={styles.title + (states.panelState === PanelStates.versions ? ' ' + styles.selected : '')}
          onClick={actions.openPageInfoPanel.bind(null, PanelStates.versions)}
        >
          { states.panelState !== PanelStates.closed && 'Versions' }
        </h2>
      }

      { states.panelState !== PanelStates.closed && <CloseButton
        handleClick={actions.closePageInfoPanel}
        className={styles.closeButton}
      /> }
    </header>

    { states.panelState === PanelStates.info && <dl className={styles.body}>
      <a className={styles.logoutButton} onClick={actions.logout}>Logout</a>
      <dt>App Version: {process.env.packageVersion}</dt>
      <dd>Build ID: {process.env.buildId || 'N/A'}</dd>
      <dd>Build Time: {process.env.buildTime || 'N/A'}</dd>

      { data.draft && <>
        <hr/>
        <dt>
          Page Draft &nbsp;&nbsp;
          <button className={styles.reloadButton} onClick={() => window.location.reload()}>
            Reload Page
          </button>
        </dt>
        <dd>{ formatDraftLabel(data.draft) }</dd>
      </> }

      { data.build && <>
        <hr/>
        <dt>Game Build &nbsp;&nbsp;</dt>
        <dd>{ formattedBuildLabel(data.build) }</dd>
      </> }

      { data.pageCacheData && <>
        <hr/>
        <dt>Page (HTML) Cache</dt>
        <div>
          { messageLine(formatTimestamp(data.pageCacheData.age), 'Age', data.pageCacheData.ageDate) }
          { messageLine(data.pageCacheData.xCache) }
          { messageLine(data.pageCacheData.cacheControl, 'Status') }
        </div>
      </> }

      { data.cmsCacheData && <>
        <hr/>
        <dt>Data (GraphQL/CMS) Cache</dt>
        <dd title={ data.cmsCacheData.cmsCacheDate }>
          CMS Cache Age: { formatTimestamp(data.cmsCacheData.cmsCacheAge) }
        </dd>
        <dd title={ data.cmsCacheData.proxyCacheDate }>
          Proxy Cache Age: { formatTimestamp(data.cmsCacheData.proxyCacheAge) }
        </dd>
        <dd>Proxy Cache Status: { data.cmsCacheData.proxyCacheStatus }</dd>
      </> }

    </dl> }

    { states.panelState === PanelStates.tools && <dl className={styles.body}>
      <dt>Toggle View</dt>
      <dd>Performance Check: <input type='checkbox' onChange={actions.toggleShowPerformanceCheck} checked={states.showDevUI}></input></dd>
      <dd>Theme Debugger: <input type='checkbox' onChange={actions.toggleShowThemeDebugger} checked={states.showThemeDevUI}></input></dd>

      <hr/>

      <VisionSettingsPanel />
    </dl> }

    { !data.isCmsPreview && states.panelState === PanelStates.versions && <dl className={styles.body}>
      <>
        {/** PAGE DRAFTS ******************************************************************/}
        { data.draft && <>
          <dt>Current version</dt>
          <dd>{ formatDraftLabel(data.draft) }</dd>
          <hr/>
        </> }

        { data.drafts && <>
          <dt>Drafts</dt>
          { !!data.drafts?.length && <div className={styles.versionsList}>
            { data.drafts.map((el) => (
              <dd key={el.id}>
                <a className={styles.draftLink}
                  href={`?version=${el.id}`}
                >{ formatDraftLabel(el) }</a>
              </dd>
            )) }
          </div> }
        </>}

        {/** GAME BUILDS ******************************************************************/}
        { data.builds?.prodRelease && <>
          <dt>Production Release</dt>
          <dd>{ formattedSimpleBuildLabel(data.builds.prodRelease) }</dd>
          <hr/>
        </> }

        { data.builds?.qaRelease && <>
          <dt>QA Release</dt>
          <dd>{ formattedSimpleBuildLabel(data.builds.qaRelease) }</dd>
          <hr/>
        </> }

        { data.builds && <>
          <dt>Latest Builds</dt>
          { !!data.builds?.recentBuilds?.length && <div className={styles.versionsList}>
            { data.builds.recentBuilds.map((el) => (
              <dd key={el.id}>
                <div className={styles.commitLink}>
                  { formattedSimpleBuildLabel(el) }
                </div>
              </dd>
            )) }
          </div> }
        </>}

        {/** NO VERSIONS ******************************************************************/}
        { !data.builds?.recentBuilds?.length && !data.drafts?.length && <>
          <dd>No other versions found</dd>
        </> }
      </>
    </dl> }
  </section>;
}
